import { colorPaletteStore } from '../../components/new-preview-sidebar/Modules/PalleteSelection/PalleteSelection.svelte';
import { thumbColorPaletteStore } from '../../components/thumbnail-sidebar/Modules/PalleteSelection/PalleteSelection.svelte';
import { getSvgColors } from '../../lib/editor/Modules/SVG/svg-utils';
import chroma from 'chroma-js';
import { v4 } from 'uuid';
const inMemoryGradientCache = [];
function areColorsClose(color1, color2, threshold) {
    const c1 = chroma(color1);
    const c2 = chroma(color2);
    const distance = chroma.distance(c1, c2);
    return distance < threshold;
}
function isColorTransparent(colorString) {
    const c1 = chroma(colorString);
    return c1.alpha() === 0;
}
function extractRGBAFromLinearGradient(backgroundImageString) {
    // Remove whitespace and 'linear-gradient' keyword
    const gradient = backgroundImageString.replace(/\s+/g, '').replace(/linear-gradient/g, '');
    // Extract the color stops from the gradient
    const colorStops = gradient.match(/rgba?\([\d\s.,]+\)/g);
    return colorStops;
}
function colorToHex(color) {
    return chroma(color).hex();
}
function colorToRgba(color) {
    const colorString = chroma(color).rgba().toString();
    return `rgba(${colorString})`;
}
function parseGradientString(gradientString) {
    // Parse the string
    const re = /linear-gradient\((\d+deg), rgba\(([\d,.\s]+)\) (\d+%), rgba\(([\d,.\s]+)\) (\d+%)\)/;
    const re2 = /linear-gradient\((\d+deg),\s*rgb(a)?\(([\d\s.,]+)\)\s*(\d+%)?,\s*rgb(a)?\(([\d\s.,]+)\)\s*(\d+%)?\)/;
    const match = re.exec(gradientString);
    const match2 = re2.exec(gradientString);
    if (match) {
        const degree = match[1];
        const color1 = match[2];
        const percent1 = match[3];
        const color2 = match[4];
        const percent2 = match[5];
        const c1 = colorToHex(`rgba(${color1})`);
        const c2 = colorToHex(`rgba(${color2})`);
        return {
            degree,
            color1: c1,
            percent1,
            color2: c2,
            percent2
        };
    }
    else if (match2) {
        const degree = match2[1];
        const color1 = match2[3];
        const percent1 = match2[4];
        const color2 = match2[6];
        const percent2 = match2[7];
        const c1 = match2[2] ? `rgba(${color1})` : `rgb(${color1})`;
        const c2 = match2[5] ? `rgba(${color2})` : `rgb(${color2})`;
        return {
            degree,
            color1: colorToHex(c1),
            percent1,
            color2: colorToHex(c2),
            percent2
        };
    }
    else {
        throw new Error('Invalid gradient string');
    }
}
export class ColorDomain {
    colorDomains = [];
    addElement(color, element, targetAttribute, targetElements) {
        let domain = null;
        this.colorDomains.forEach(d => {
            if (domain)
                return;
            if (areColorsClose(d.color, color, 20))
                domain = d;
        });
        if (!domain) {
            this.colorDomains.push({
                color,
                elements: [
                    {
                        element,
                        isSVG: element instanceof SVGElement,
                        targetAttribute,
                        targetElements
                    }
                ]
            });
        }
        else {
            domain.elements.push({
                element,
                isSVG: element instanceof SVGElement,
                targetAttribute,
                targetElements
            });
        }
        return domain;
    }
    getDomain(color) {
        return this.colorDomains.find(domain => domain.color === color);
    }
    get colors() {
        return this.colorDomains.map(domain => domain.color);
    }
}
export default class ColorLoader {
    node;
    colorDomains;
    constructor(node) {
        this.node = node;
        this.readColors();
    }
    get colors() {
        return [];
    }
    readColors() {
        this.colorDomains = new ColorDomain();
        this.readDomElementColors();
    }
    readDomElementColors() {
        function getUniqueColors(array) {
            return array.map(color => color.color?.hex()).filter((value, index, self) => self.indexOf(value) === index);
        }
        this.node.querySelectorAll('.shapeOuter > svg').forEach(svg => {
            const rawColors = getSvgColors(svg);
            const colors = {
                fills: getUniqueColors(rawColors.fills),
                strokes: getUniqueColors(rawColors.strokes),
                stops: getUniqueColors(rawColors.stops)
            };
            Object.keys(colors).forEach((key) => {
                const itemColors = colors[key];
                itemColors.forEach((c) => {
                    let targetElements = [];
                    if (key === 'stops') {
                        const elements = svg.querySelectorAll('stop');
                        Array.from(elements).forEach(stop => {
                            if (stop.getAttribute('stop-color') == c)
                                targetElements.push(stop);
                        });
                    }
                    this.colorDomains.addElement(c, svg, key, targetElements);
                });
            });
        });
        // Compute for background colors
        this.node.querySelectorAll('.userTextClass, #bkgImageContainter')?.forEach(textElement => {
            const computedStyle = getComputedStyle(textElement);
            const borderColor = computedStyle.borderColor;
            if (borderColor && borderColor !== 'transparent') {
                this.colorDomains.addElement(borderColor, textElement, 'border');
            }
            const raw = computedStyle.backgroundImage;
            if (raw.includes('linear-gradient')) {
                // First we check if there is a linear gradient in the element, if there is we extract the colors
                const colors = extractRGBAFromLinearGradient(raw);
                if (colors && colors.length > 0) {
                    let colorHexes = [];
                    colors.forEach((color, key) => {
                        if (color.includes('rgb') && !isColorTransparent(color)) {
                            const c = chroma(color);
                            this.colorDomains.addElement(c.hex(), textElement, 'backgroundImage');
                            colorHexes.push(c.hex());
                        }
                    });
                    const id = v4();
                    try {
                        const data = parseGradientString(textElement.style.backgroundImage);
                        inMemoryGradientCache.push({
                            id,
                            ...data,
                            originalColor1: data.color1,
                            originalColor2: data.color2
                        });
                        textElement.setAttribute('bg-gradient', id);
                    }
                    catch (err) {
                        console.warn('err', err);
                    }
                }
            }
            else {
                // Check if there is a background color the reason we add this into an else block is that we want the gradients to take precedence
                const raw = getComputedStyle(textElement).backgroundColor;
                // There is a bug that causes the background color to be returned as rgba(255,255,255,0) when the background color is transparent so we also need to check for isColorTransparent
                if (raw && !isColorTransparent(raw)) {
                    const c = chroma(raw);
                    this.colorDomains.addElement(c.hex(), textElement, 'backgroundColor');
                }
            }
        });
        // Compute for text colors
        this.node.querySelectorAll('.userTextClass .dynamic')?.forEach(textElement => {
            const raw = getComputedStyle(textElement).color;
            const background = getComputedStyle(textElement).background;
            if (background.includes('linear-gradient')) {
                const colors = extractRGBAFromLinearGradient(background);
                if (colors && colors.length > 0) {
                    let colorHexes = [];
                    colors.forEach((color, key) => {
                        if (color.includes('rgb')) {
                            const c = chroma(color);
                            this.colorDomains.addElement(c.hex(), textElement, 'background');
                            colorHexes.push(c.hex());
                        }
                    });
                    const id = v4();
                    try {
                        const data = parseGradientString(textElement.style.background);
                        inMemoryGradientCache.push({
                            id,
                            ...data,
                            originalColor1: data.color1,
                            originalColor2: data.color2
                        });
                        textElement.setAttribute('bg-gradient', id);
                    }
                    catch (err) {
                        console.warn('err', err);
                    }
                }
            }
            const c = chroma(raw);
            this.colorDomains.addElement(c.hex(), textElement);
        });
        colorPaletteStore.set(this.colorDomains.colors);
        thumbColorPaletteStore.set(this.colorDomains.colors);
    }
    changeSVGColor(svg, target, color, old, targetElements) {
        switch (target) {
            case 'stops':
                if (targetElements && targetElements.length)
                    targetElements.forEach(stop => {
                        stop.setAttribute('stop-color', color);
                    });
                break;
        }
    }
    changeBackgroundImageGradient(element, newColor, oldColor) {
        const cached = inMemoryGradientCache.find(c => c.id === element.getAttribute('bg-gradient'));
        if (!cached)
            return;
        if (areColorsClose(cached.originalColor1, oldColor, 20)) {
            cached.color1 = newColor;
        }
        if (areColorsClose(cached.originalColor2, oldColor, 20)) {
            cached.color2 = newColor;
        }
        const replacement = `linear-gradient(${cached.degree}, ${colorToRgba(cached.color1)} ${cached.percent1}, ${colorToRgba(cached.color2)} ${cached.percent2})`;
        element.style.backgroundImage = replacement;
    }
    changeBackgroundGradient(element, newColor, oldColor) {
        const cached = inMemoryGradientCache.find(c => c.id === element.getAttribute('bg-gradient'));
        if (!cached)
            return;
        if (areColorsClose(cached.originalColor1, oldColor, 20)) {
            cached.color1 = newColor;
        }
        if (areColorsClose(cached.originalColor2, oldColor, 20)) {
            cached.color2 = newColor;
        }
        const replacement = `linear-gradient(${cached.degree}, ${colorToRgba(cached.color1)} ${cached.percent1 ? cached.percent1 : ''}, ${colorToRgba(cached.color2)} ${cached.percent2 ? cached.percent2 : ''}) text`;
        element.style.background = replacement;
    }
    changeDivColor(divElement, color, targetAttribute, oldColor) {
        if (targetAttribute) {
            switch (targetAttribute) {
                case 'backgroundImage':
                    if (oldColor)
                        this.changeBackgroundImageGradient(divElement, color, oldColor);
                    break;
                case 'backgroundColor':
                    divElement.style.backgroundColor = color;
                    break;
                case 'border':
                    divElement.style.border = divElement.style.border.replace(oldColor, color);
                    break;
                case 'background':
                    if (oldColor)
                        this.changeBackgroundGradient(divElement, color, oldColor);
                    break;
            }
        }
        else {
            divElement.style.color = color;
        }
    }
    changeColor(oldColor, newColor) {
        const domain = this.colorDomains.getDomain(oldColor);
        domain?.elements.forEach(element => {
            if (element.isSVG) {
                this.changeSVGColor(element.element, element.targetAttribute, newColor, oldColor, element.targetElements);
            }
            else
                this.changeDivColor(element.element, newColor, element.targetAttribute, oldColor);
        });
    }
    getDomain(color) {
        return this.colorDomains.getDomain(color);
    }
}
